import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    PieChart, Pie, Cell, Legend, BarChart, Bar,
} from "recharts";
import { Users, MousePointerClick, Activity, Smartphone, Globe, Bot } from "lucide-react";

const SOURCE_COLORS = { direct: "#0369a1", organic: "#059669", social: "#7c3aed", referral: "#d97706" };
const DEVICE_COLORS = ["#0369a1", "#059669", "#f59e0b"];

function KPI({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="bg-white border border-stone-200 p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
                <Icon className="w-4 h-4 text-sky-700" />
            </div>
            <div className="font-serif text-3xl text-stone-900">{value}</div>
            {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function AdminTraffic() {
    const [range, setRange] = useState(30);
    const [summary, setSummary] = useState(null);
    const [daily, setDaily] = useState([]);
    const [topPages, setTopPages] = useState([]);
    const [sources, setSources] = useState([]);
    const [devices, setDevices] = useState([]);
    const [bots, setBots] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async (days) => {
        setBusy(true);
        try {
            const [s, d, p, sr, dv, bt] = await Promise.all([
                api.get("/traffic/summary"),
                api.get("/traffic/daily", { params: { days } }),
                api.get("/traffic/top-pages", { params: { days } }),
                api.get("/traffic/sources", { params: { days } }),
                api.get("/traffic/devices", { params: { days } }),
                api.get("/traffic/bot-activity", { params: { days } }),
            ]);
            setSummary(s.data);
            setDaily(d.data.rows || []);
            setTopPages(p.data.rows || []);
            setSources(sr.data.rows || []);
            setDevices(dv.data.rows || []);
            setBots(bt.data.rows || []);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(range); /* eslint-disable-next-line */ }, [range]);

    return (
        <div className="space-y-6" data-testid="admin-traffic-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-sky-700">Analytics</div>
                    <h1 className="font-serif text-4xl text-stone-900">Traffic</h1>
                    <p className="text-sm text-stone-500 mt-1">Server-side first-party analytics. Powered by <code className="text-xs bg-stone-100 px-1">/traffic/hit</code> — no third-party cookies.</p>
                </div>
                <div className="flex gap-2">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setRange(d)}
                            className={`text-xs px-3 py-1.5 border ${range === d ? "bg-sky-700 text-stone-50 border-sky-700" : "bg-white border-stone-300 hover:bg-stone-50"}`}
                            data-testid={`traffic-range-${d}`}
                        >
                            {d} days
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI row */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI icon={Activity} label="Active now (5 min)" value={summary.realtime?.visits ?? 0} sub={`${summary.realtime?.unique_visitors ?? 0} unique`} testId="kpi-realtime" />
                    <KPI icon={MousePointerClick} label="Today" value={summary.today?.visits ?? 0} sub={`${summary.today?.unique_visitors ?? 0} unique`} testId="kpi-today" />
                    <KPI icon={Users} label="Last 7 days" value={summary.week?.visits ?? 0} sub={`${summary.week?.unique_visitors ?? 0} unique`} testId="kpi-week" />
                    <KPI icon={Globe} label="Last 30 days" value={summary.month?.visits ?? 0} sub={`${summary.month?.unique_visitors ?? 0} unique`} testId="kpi-month" />
                </div>
            )}

            {/* Trend chart */}
            <div className="bg-white border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Daily visits</div>
                <div style={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                        <LineChart data={daily}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip />
                            <Line type="monotone" dataKey="visits" stroke="#0369a1" strokeWidth={2} dot={false} name="Visits" />
                            <Line type="monotone" dataKey="unique" stroke="#059669" strokeWidth={2} dot={false} name="Unique" />
                            <Line type="monotone" dataKey="sessions" stroke="#d97706" strokeWidth={2} dot={false} name="Sessions" />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Sources pie */}
                <div className="bg-white border border-stone-200 p-5">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Traffic sources</div>
                    {sources.length ? (
                        <div style={{ width: "100%", height: 260 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={sources} dataKey="visits" nameKey="source" outerRadius={90} label>
                                        {sources.map((s) => (
                                            <Cell key={s.source} fill={SOURCE_COLORS[s.source] || "#78716c"} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <div className="text-sm text-stone-500 py-16 text-center">No source data yet</div>}
                </div>

                {/* Devices */}
                <div className="bg-white border border-stone-200 p-5">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Devices</div>
                    {devices.length ? (
                        <div style={{ width: "100%", height: 260 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={devices} dataKey="visits" nameKey="device" innerRadius={55} outerRadius={90} label>
                                        {devices.map((d, i) => (
                                            <Cell key={d.device} fill={DEVICE_COLORS[i % DEVICE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <div className="text-sm text-stone-500 py-16 text-center flex items-center justify-center"><Smartphone className="w-4 h-4 mr-2" />No device data yet</div>}
                </div>
            </div>

            {/* Top pages */}
            <div className="bg-white border border-stone-200">
                <div className="p-5 pb-3 text-xs uppercase tracking-widest text-stone-500">Top pages</div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-y border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Path</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Visits</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Unique</th>
                        </tr>
                    </thead>
                    <tbody>
                        {topPages.length === 0 ? (
                            <tr><td colSpan={3} className="p-8 text-center text-stone-400">No page views yet in this range.</td></tr>
                        ) : topPages.map((p) => (
                            <tr key={p.path} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`traffic-page-${p.path}`}>
                                <td className="p-3 font-mono text-stone-800">{p.path}</td>
                                <td className="p-3 text-right font-mono">{p.visits}</td>
                                <td className="p-3 text-right font-mono text-emerald-700">{p.unique}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* AI bots */}
            <div className="bg-white border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-700" /> AI crawler activity
                </div>
                {bots.length === 0 ? (
                    <p className="text-sm text-stone-500">No AI crawler visits detected yet. GPTBot, ClaudeBot, PerplexityBot etc. will show up here once they crawl the site — manage their allow/deny policy in the GEO tab.</p>
                ) : (
                    <div style={{ width: "100%", height: Math.max(180, bots.length * 34) }}>
                        <ResponsiveContainer>
                            <BarChart data={bots} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                <XAxis type="number" tick={{ fontSize: 11 }} />
                                <YAxis dataKey="bot" type="category" tick={{ fontSize: 11 }} width={110} />
                                <Tooltip />
                                <Bar dataKey="visits" fill="#7c3aed" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {busy && <div className="text-xs text-stone-500">Refreshing…</div>}
        </div>
    );
}
